import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

const slideVariants = {
	hidden: {
		x: "100%",
		transition: {
			duration: 0.3,
			ease: [0.23, 1, 0.32, 1] as [number, number, number, number],
		},
	},
	visible: {
		x: "0%",
		transition: {
			duration: 0.5,
			ease: [0.32, 0.72, 0, 1] as [number, number, number, number],
		},
	},
};

const reducedVariants = {
	hidden: {
		opacity: 0,
		transition: { duration: 0.2 },
	},
	visible: {
		opacity: 1,
		transition: { duration: 0.2 },
	},
};

type MobileNavPanelProps = {
	/**
	 * Identifies the currently rendered view. Changing this crossfades the
	 * inner content without re-running the panel's slide-in animation, since
	 * the panel element itself stays mounted across view switches.
	 */
	viewKey: string;
	ariaLabel: string;
	children: React.ReactNode;
};

/**
 * Shared slide-out shell for the cross-entity mobile navigation. The panel
 * slides in once when mounted and slides out when unmounted (driven by the
 * parent's AnimatePresence). Switching between views only swaps the children
 * via an opacity crossfade so the slide animation never repeats.
 */
const MobileNavPanel = ({
	viewKey,
	ariaLabel,
	children,
}: MobileNavPanelProps) => {
	const shouldReduceMotion = useReducedMotion();
	const wrapperVariants = shouldReduceMotion ? reducedVariants : slideVariants;
	const fadeDuration = shouldReduceMotion ? 0 : 0.15;

	const panelRef = useRef<HTMLElement>(null);

	// The scrolling container stays mounted across view switches, so its scroll
	// offset persists. Reset it to the top whenever the view changes so the new
	// menu always opens at its first item instead of the previous scroll offset.
	useEffect(() => {
		panelRef.current?.scrollTo({ top: 0 });
	}, [viewKey]);

	return (
		<motion.nav
			ref={panelRef}
			id="primary-mobile-nav"
			data-testid="primary-mobile-nav"
			aria-label={ariaLabel}
			initial="hidden"
			animate="visible"
			exit="hidden"
			variants={wrapperVariants}
			className="z-mobile-nav fixed left-0 flex h-full w-full flex-[1_1_auto] flex-col overflow-y-scroll bg-white pb-16 top-[calc(var(--banner-height)_+_60px)]"
		>
			<AnimatePresence mode="wait" initial={false}>
				<motion.div
					key={viewKey}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: fadeDuration }}
					className="flex w-full flex-1 flex-col"
				>
					{children}
				</motion.div>
			</AnimatePresence>
		</motion.nav>
	);
};

export default MobileNavPanel;
